import {
    EMETLAssessmentTools,
    EMETLDetailedAssessment,
} from "../Model/EMETLAssessment.m"

import {
    EMETLContext,
} from "../Model/Contexts/AppContext"

import { 
    AssessmentDetail,
    AssessmentTask, 
} from "../API"

import PersonnelVM from "../ViewModel/Personnel.vm"

import {
    fetchUserAttributes,
} from "aws-amplify/auth"

export type StyleModel = {
    container: object,
    light: object,
    dark: object,
}

export class CreateAssessment extends EMETLAssessmentTools {
    /*** Private Members ***/
    #personnelManager = PersonnelVM
    
    /*** Protected Members ***/
    
    /*** Private Methods ***/
    
    /*** Protected Methods ***/
    
    /**
     * Initialize and perform setup for the class
     */
    protected _init = () => {
        // Initialize or set all members based on constructor argument values 
    }
    
    /*** Public Methods ***/
    
    /**
     * Class constructor. Calls _init() on the superclass. Add parameters as needed
     */
    public constructor() {
        super()
        this._init()
    }

    public createNewAssessmentAndDetails = async (
        newAssessment: EMETLDetailedAssessment,
        doneEditing: (success: boolean) => void,
        assessmentDetails: AssessmentDetail,
        assessmentTasks: string = null,
        assessed: string = null,
    ) : Promise<number | null> => {
        const {
            loading: {
                update: setLoading,
                setMessage: setLoadingMessage,
            },
            banner: {
                setError,
            },
        } = this.ctx as EMETLContext
        const userAttributes = await fetchUserAttributes()
        if (!userAttributes) {
            setError("Error fetching user attributes.")
            doneEditing(false)
            return
        }
        const username = userAttributes?.sub
        setLoadingMessage(`Creating ${newAssessment?.Title}`)
        const createResp = await this.createAssessment(newAssessment, username, assessmentTasks, assessed)
        if (!createResp) {
            setError("Error creating assessment.")
            setLoading(false)
            setLoadingMessage("")
            doneEditing(false)

            return null
        }
        assessmentDetails.AssessmentID = createResp.ID
        if (!newAssessment.AssessmentDetailID) {
            setLoadingMessage(`Creating details for ${newAssessment?.Title}`)
            await this.createAssessmentDetail(
                assessmentDetails
            )
        } else {
            setError("Assessment details already exist for new assessment. Please inform the administrator.")
            setLoading(false)
            setLoadingMessage("")
            doneEditing(false)

            return null
        }

        return createResp.ID
    }

    public updateExistingAssessmentAndDetails = async (
        existingAssessment: EMETLDetailedAssessment,
        doneEditing: (success: boolean) => void,
        assessmentDetails: AssessmentDetail,
        assessmentTasks: string = null,
        deleteAssessmentTaskIDs: string = null,
        assessed: string = null,
        deleteAssessed: string = null,
    ) => {
        const {
            loading: {
                update: setLoading,
                setMessage: setLoadingMessage,
            },
            banner: {
                setError,
                setInfo,
            },
        } = this.ctx as EMETLContext

        setLoadingMessage(`Updating ${existingAssessment?.Title}`)
        const userAttributes = await fetchUserAttributes()
        if (!userAttributes) {
            setError("Error fetching user attributes.")
            doneEditing(false)
            return
        }
        const username = userAttributes?.sub
        const resp = await this.updateAssessment(
            existingAssessment, 
            username, 
            assessmentTasks, 
            deleteAssessmentTaskIDs, 
            assessed, 
            deleteAssessed,
        )
        
        if (!resp) {
            setError("Error updating assessment.")
            setLoading(false)
            setLoadingMessage("")
            doneEditing(false)

            return
        }
        if (this.anyDetailsAreSet(existingAssessment)) {
            if (existingAssessment.AssessmentDetailID) {
                setLoadingMessage(`Updating details for ${existingAssessment?.Title}`)
                const updateResp = await this.updateAssessmentDetail(
                    assessmentDetails,
                )
                if (!updateResp) {
                    setError("Error updating assessment details.")
                    setLoading(false)
                    setLoadingMessage("")
                    doneEditing(false)

                    return
                }
            } else {
                setLoadingMessage(`Creating details for ${existingAssessment?.Title}`)
                const createResp = await this.createAssessmentDetail(
                    assessmentDetails,
                )

                if (!createResp) {
                    setError("Error creating assessment details.")
                    setLoading(false)
                    setLoadingMessage("")
                    doneEditing(false)

                    return
                }
            }
        }
        doneEditing(true)
        setInfo(`${existingAssessment?.Title} updated successfully.`)
    }

    public get PersonnelManager() {
        return this.#personnelManager
    }
}

// Instantiate the VM and export the instance
const CreateAssessmentVM = new CreateAssessment()

export default CreateAssessmentVM
